import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { message } from 'antd';

function useFlagCompnentMounted() {
  const mountedFlag = useRef(true);
  useEffect(() => {
    return () => {
      mountedFlag.current = false;
    };
  }, []);

  return mountedFlag;
}

type ResType<DataType> = {
  ret: string;
  data: DataType;
  msg: string;
};

type Options<DataType> = {
  manual?: boolean;
  defaultParams?: any;
  mutate?(data: DataType | undefined): DataType | undefined;
  afterLoad?(data: ResType<DataType>): { ok: boolean; data: DataType; msg?: string };
  onSuccess?(data: ResType<DataType>): void;
  onError?(data?: ResType<DataType>): void;
  silent?: boolean;
};

function defaultAfterLoad<DataType>(res: ResType<DataType>) {
  const { ret, data, msg } = res;
  if (ret === '0') return { ok: true, data, msg };
  return { ok: false, data, msg };
}

function defaultOnSuccess() {}

// TODO: 能不能根据request自动推断类型？
function useRequest<DataType extends object>(request: any, options?: Options<DataType>) {
  const flagCompnentMounted = useFlagCompnentMounted();

  const {
    manual = false,
    mutate = (data: DataType | undefined) => data,
    defaultParams,
    afterLoad = defaultAfterLoad,
    onSuccess = defaultOnSuccess,
    onError = defaultOnSuccess,
    silent = false,
  } = useMemo(
    () => options || {},
    // eslint-disable-next-line
    []
  );
  // eslint-disable-next-line
  const innerRequest = useCallback(request, []);

  const [state, setState] = useState<{ data?: DataType; loading: boolean; error?: Error }>({
    data: undefined,
    loading: !manual,
    error: undefined,
  });

  const run = useCallback(
    (...params: any[]) => {
      setState((innerState) => ({ ...innerState, loading: true }));
      return innerRequest(...params)
        .then((res: ResType<DataType>) => {
          if (!flagCompnentMounted.current) return;

          const { ok, data, msg } = afterLoad(res);
          if (ok) {
            setState({ data, loading: false, error: undefined });
            onSuccess(res);
          } else {
            setState({ data, loading: false, error: new Error(msg) as any });
            onError();
            !silent && message.error(msg);
          }
        })
        .catch((ex: any) => {
          if (!flagCompnentMounted.current) return;
          setState({ data: undefined, loading: false, error: ex });
          onError();
          console.error(ex);
          !silent && message.error('系统异常请稍后重试~');
        });
    },
    [innerRequest, flagCompnentMounted, afterLoad, onSuccess, onError, silent]
  );

  useEffect(() => {
    if (manual) return;

    run(defaultParams);
  }, [run, manual, defaultParams]);

  return { ...state, data: mutate(state.data), run };
}

export default useRequest;
